/**
 * MessageThread Component
 *
 * Chat display showing messages in chat bubble style with
 * timestamps grouped by day, read receipts, infinite scroll,
 * typing indicators, and Phase 5 advanced features.
 * Supports both direct messages and group conversations.
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import {
  Loader2,
  MoreVertical,
  Archive,
  VolumeX,
  Volume2,
  ChevronDown,
  Download,
  Users,
  Info,
  UserPlus,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';
import { EditMessageModal } from './EditMessageModal';
import { ConversationExport } from './ConversationExport';
import { GroupInfoModal } from './GroupInfoModal';
import { AddMembersModal } from './AddMembersModal';
import { ComposingHeader } from './ComposingHeader';

import { getErrorMessageOrFallback } from '@/utils/errors';
import { logger } from '@/utils/logger';
import type { Conversation, Message, PendingMessage, ConversationParticipant } from '@/types/messaging';

interface MessageThreadProps {
  conversation: Conversation | null;
  messages: Message[];
  pendingMessages: PendingMessage[];
  typingUserIds: string[];
  currentUserId: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onSendMessage: (content: string, replyToId?: string) => Promise<void>;
  onRetryPendingMessage: (tempId: string) => Promise<void>;
  onRemovePendingMessage: (tempId: string) => void;
  onEditMessage: (messageId: string, content: string) => Promise<void>;
  onDeleteMessage: (messageId: string) => Promise<void>;
  onLoadMore: () => Promise<void>;
  onTypingStart: () => void;
  onTypingStop: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onMute: (mutedUntil: Date) => void;
  onUnmute: () => void;
  isSending: boolean;
  // Composing mode props (iMessage-style flow)
  isComposing?: boolean;
  composingRecipients?: ConversationParticipant[];
  onAddComposingRecipient?: (recipient: ConversationParticipant) => void;
  onRemoveComposingRecipient?: (userId: string) => void;
  onCancelComposing?: () => void;
  onOpenExistingConversation?: (conversation: Conversation) => void;
  // Add people to conversation
  onConvertToGroup?: (conversationId: string) => void;
}

// Mute duration options
const MUTE_DURATIONS = [
  { label: '1 hour', hours: 1 },
  { label: '8 hours', hours: 8 },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: 'Until I turn it back on', hours: 24 * 365 },
];

/**
 * Get user initials
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Format date header
 */
const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
};

const isPendingMessage = (message: Message | PendingMessage): message is PendingMessage => {
  return 'tempId' in message;
};

const getReverseScrollDistances = (element: HTMLElement) => {
  const maxScrollDistance = Math.max(element.scrollHeight - element.clientHeight, 0);
  const distanceFromBottom = Math.min(Math.abs(element.scrollTop), maxScrollDistance);
  const distanceFromOldestEdge = Math.max(maxScrollDistance - distanceFromBottom, 0);

  return {
    distanceFromBottom,
    distanceFromOldestEdge,
    maxScrollDistance,
  };
};

const logLoadMoreFailure = (error: unknown): void => {
  logger.warn('Message thread load-more failed', {
    error: getErrorMessageOrFallback(error, 'Failed to load older messages'),
  });
};

/**
 * Group messages by date
 */
const groupMessagesByDate = (
  messages: Message[],
  pendingMessages: PendingMessage[]
): Array<{ date: string; items: (Message | PendingMessage)[] }> => {
  // Combine and sort all messages (pending messages first, then by time descending)
  const allMessages: (Message | PendingMessage)[] = [
    ...pendingMessages,
    ...messages,
  ];

  // Group by date
  const groups = new Map<string, (Message | PendingMessage)[]>();

  allMessages.forEach((msg) => {
    const dateKey = format(new Date(msg.createdAt), 'yyyy-MM-dd');
    const group = groups.get(dateKey);

    if (group) {
      group.push(msg);
    } else {
      groups.set(dateKey, [msg]);
    }
  });

  // Convert to array and sort groups by date (newest first)
  return Array.from(groups.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, items]) => ({
      date,
      items: items.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    }));
};

export const MessageThread: React.FC<MessageThreadProps> = ({
  conversation,
  messages,
  pendingMessages,
  typingUserIds,
  currentUserId,
  isLoading,
  isLoadingMore,
  hasMore,
  onSendMessage,
  onRetryPendingMessage,
  onRemovePendingMessage,
  onEditMessage,
  onDeleteMessage,
  onLoadMore,
  onTypingStart,
  onTypingStop,
  onArchive,
  onUnarchive,
  onMute,
  onUnmute,
  isSending,
  // Composing mode props
  isComposing = false,
  composingRecipients = [],
  onAddComposingRecipient,
  onRemoveComposingRecipient,
  onCancelComposing,
  onOpenExistingConversation,
  // Add people props
  onConvertToGroup,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Phase 5: Edit message modal state
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);

  // Phase 5: Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Phase 5.5: Group info modal state
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  // Add members modal state (for adding people to groups)
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  // Group messages by date
  const messageGroups = useMemo(
    () => groupMessagesByDate(messages, pendingMessages),
    [messages, pendingMessages]
  );

  // Get typing user names
  const typingUserNames = useMemo(() => {
    if (!conversation || typingUserIds.length === 0) return [];

    // For group conversations, look up names from participants
    if (conversation.isGroup && conversation.participants) {
      return typingUserIds
        .map((id) => {
          const participant = conversation.participants?.find((p) => p.id === id);
          return participant?.firstName || participant?.username || null;
        })
        .filter((name): name is string => !!name);
    }

    // For direct conversations, the only remote typing user is the single participant.
    // Prefer the exact id match, but tolerate stale participant ids from cached conversation data.
    if (
      conversation.participant &&
      (typingUserIds.includes(conversation.participant.id) ||
        typingUserIds.some((id) => id !== currentUserId))
    ) {
      return [conversation.participant.firstName || conversation.participant.username];
    }
    return [];
  }, [conversation, currentUserId, typingUserIds]);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = useCallback((behavior: 'smooth' | 'instant' | 'auto' = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  /**
   * Handle scroll for infinite loading and show/hide scroll-to-bottom button
   */
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const { distanceFromBottom, distanceFromOldestEdge, maxScrollDistance } =
        getReverseScrollDistances(target);

      // Show scroll-to-bottom button when scrolled up
      const isNearBottom = distanceFromBottom < 100;
      setShowScrollToBottom(!isNearBottom);

      if (
        maxScrollDistance > 0 &&
        distanceFromOldestEdge < 100 &&
        hasMore &&
        !isLoadingMore
      ) {
        onLoadMore().catch(logLoadMoreFailure);
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  /**
   * Scroll to bottom on new messages
   */
  useEffect(() => {
    if (messages.length > 0 || pendingMessages.length > 0) {
      // Only auto-scroll if near bottom
      const scrollArea = scrollAreaRef.current;
      if (scrollArea) {
        const { distanceFromBottom } = getReverseScrollDistances(scrollArea);
        const isNearBottom = distanceFromBottom < 150;
        if (isNearBottom) {
          scrollToBottom();
        }
      }
    }
  }, [messages.length, pendingMessages.length, scrollToBottom]);

  /**
   * Handle reply
   */
  const handleReply = (message: Message) => {
    setReplyingTo(message);
  };

  /**
   * Handle send with reply
   */
  const handleSend = async (content: string, replyToId?: string) => {
    await onSendMessage(content, replyToId);
    setReplyingTo(null);
    scrollToBottom();
  };

  /**
   * Handle edit message - opens the edit modal
   */
  const handleEditClick = useCallback((message: Message) => {
    setEditingMessage(message);
  }, []);

  /**
   * Handle save edited message
   */
  const handleSaveEdit = useCallback(
    async (messageId: string, content: string) => {
      await onEditMessage(messageId, content);
    },
    [onEditMessage]
  );

  /**
   * Handle mute with duration
   */
  const handleMuteWithDuration = useCallback(
    (hours: number) => {
      const mutedUntil = new Date();
      mutedUntil.setHours(mutedUntil.getHours() + hours);
      onMute(mutedUntil);
    },
    [onMute]
  );

  // Empty state - no conversation selected and not composing
  if (!conversation && !isComposing) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-muted/20 p-6">
        <div className="h-16 w-16 rounded-full mb-4 flex items-center justify-center bg-primary/10">
          <svg
            className="h-8 w-8 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          Choose a conversation from the list or start a new one to begin messaging
        </p>
      </div>
    );
  }

  // Composing mode - show empty chat with recipient selector
  if (isComposing && !conversation) {
    return (
      <div className="h-full flex flex-col bg-background">
        {/* Composing header */}
        {onAddComposingRecipient && onRemoveComposingRecipient && onCancelComposing && (
          <ComposingHeader
            recipients={composingRecipients}
            onAddRecipient={onAddComposingRecipient}
            onRemoveRecipient={onRemoveComposingRecipient}
            onOpenExisting={onOpenExistingConversation ?? (() => {})}
            onCancel={onCancelComposing}
          />
        )}

        {/* Empty messages area with placeholder */}
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          {composingRecipients.length === 0 ? (
            <>
              <div className="h-12 w-12 rounded-full mb-4 flex items-center justify-center bg-primary/10">
                <svg
                  className="h-6 w-6 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Search for a connection to start a conversation
              </p>
            </>
          ) : (
            <>
              <div className="h-12 w-12 rounded-full mb-4 flex items-center justify-center bg-primary/10">
                <svg
                  className="h-6 w-6 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Type a message to start the conversation
              </p>
            </>
          )}
        </div>

        {/* Message input - enabled when recipients selected */}
        <MessageInput
          onSend={handleSend}
          onTypingStart={onTypingStart}
          onTypingStop={onTypingStop}
          isSending={isSending}
          disabled={composingRecipients.length === 0}
          placeholder={composingRecipients.length === 0 ? 'Select a recipient first...' : 'Type a message...'}
        />
      </div>
    );
  }

  // After the above early returns, conversation is guaranteed to be non-null
  if (!conversation) {
    return null;
  }

  const isGroup = conversation.isGroup;
  const participant = conversation.participant;

  // Get display info based on conversation type
  const displayName = isGroup
    ? conversation.groupName || 'Unnamed Group'
    : `${participant?.firstName || ''} ${participant?.lastName || ''}`.trim() || participant?.username || 'Unknown';

  const initials = isGroup
    ? null // Groups use icon
    : getInitials(participant?.firstName, participant?.lastName, participant?.username);

  const avatarSrc = isGroup ? conversation.groupAvatar : participant?.avatar;
  const isOnline = !isGroup && participant?.isOnline;
  const memberCount = isGroup ? conversation.participants?.length || 0 : 0;

  const isArchived = !!conversation.archivedAt;
  const isMuted = conversation.mutedUntil && new Date(conversation.mutedUntil) > new Date();

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header - Conditional rendering for composing mode vs regular conversation */}
      {isComposing && onAddComposingRecipient && onRemoveComposingRecipient && onCancelComposing ? (
        <ComposingHeader
          recipients={composingRecipients}
          onAddRecipient={onAddComposingRecipient}
          onRemoveRecipient={onRemoveComposingRecipient}
          onOpenExisting={onOpenExistingConversation ?? (() => {})}
          onCancel={onCancelComposing}
        />
      ) : (
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-10 w-10">
                {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
                <AvatarFallback
                  style={{
                    backgroundColor: 'hsl(var(--primary) / 0.1)',
                    color: 'hsl(var(--primary))',
                  }}
                  className="text-sm font-medium"
                >
                  {isGroup ? <Users className="h-5 w-5" /> : initials}
                </AvatarFallback>
              </Avatar>
              {isOnline && (
                <span
                  className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background"
                  style={{ backgroundColor: 'hsl(var(--feedback-success))' }}
                />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground">{displayName}</h3>
              <p className="text-xs text-muted-foreground">
                {isGroup
                  ? `${memberCount} member${memberCount !== 1 ? 's' : ''}`
                  : isOnline
                  ? 'Online'
                  : 'Offline'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Add people button for groups (quick access) */}
            {isGroup && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowAddMembersModal(true)}
                title="Add people"
              >
                <UserPlus className="h-4 w-4" />
                <span className="sr-only">Add people</span>
              </Button>
            )}

            {/* Add people button for 1:1 conversations (converts to group) */}
            {!isGroup && onConvertToGroup && conversation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => onConvertToGroup(conversation.id)}
                title="Add people to create a group"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                <span className="text-sm">Add people</span>
              </Button>
            )}

            {/* Group Info button */}
            {isGroup && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setShowGroupInfo(true)}
              >
                <Info className="h-4 w-4 mr-1" />
                <span className="text-sm">Info</span>
              </Button>
            )}

            {/* Muted badge */}
            {isMuted && (
              <Badge variant="secondary" className="text-xs">
                <VolumeX className="h-3 w-3 mr-1" />
                Muted
              </Badge>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">Conversation options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Archive/Unarchive */}
                {isArchived ? (
                  <DropdownMenuItem onClick={onUnarchive}>
                    <Archive className="h-4 w-4 mr-2" />
                    Unarchive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={onArchive}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                )}

                {/* Mute with duration submenu */}
                {isMuted ? (
                  <DropdownMenuItem onClick={onUnmute}>
                    <Volume2 className="h-4 w-4 mr-2" />
                    Unmute
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <VolumeX className="h-4 w-4 mr-2" />
                      Mute notifications
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {MUTE_DURATIONS.map((duration) => (
                          <DropdownMenuItem
                            key={duration.label}
                            onClick={() => handleMuteWithDuration(duration.hours)}
                          >
                            {duration.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}

                <DropdownMenuSeparator />

                {/* Export conversation */}
                <DropdownMenuItem onClick={() => setShowExportModal(true)}>
                  <Download className="h-4 w-4 mr-2" />
                  Export conversation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Messages area */}
      <div
        ref={scrollAreaRef}
        data-testid="message-thread-scroll-area"
        className="flex-1 overflow-y-auto p-4 flex flex-col-reverse"
        onScroll={handleScroll}
      >
        {/* Bottom anchor */}
        <div ref={messagesEndRef} />

        {/* Typing indicator */}
        {typingUserNames.length > 0 && (
          <div className="mb-2">
            <TypingIndicator userNames={typingUserNames} />
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messageGroups.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8">
            <p className="text-sm text-muted-foreground mb-2">No messages yet</p>
            <p className="text-xs text-muted-foreground">
              Send a message to start the conversation
            </p>
          </div>
        ) : (
          <>
            {/* Load more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Message groups (displayed in reverse order) */}
            {messageGroups.map((group) => (
              <div key={group.date} className="mb-4">
                {/* Messages */}
                <div className="space-y-2 flex flex-col-reverse">
                  {group.items.map((msg, index) => {
                    const prevMsg = group.items[index + 1];

                    if (isPendingMessage(msg)) {
                      return (
                        <MessageBubble
                          key={msg.tempId}
                          message={msg}
                          isOwn
                          showAvatar={false}
                          isGroupMessage={isGroup}
                          currentUserId={currentUserId}
                          onRetryPendingMessage={onRetryPendingMessage}
                          onRemovePendingMessage={onRemovePendingMessage}
                        />
                      );
                    }

                    const isOwn = msg.sender.id === currentUserId;
                    const showAvatar =
                      !isOwn &&
                      (!prevMsg ||
                        isPendingMessage(prevMsg) ||
                        prevMsg.sender.id !== msg.sender.id);

                    return (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        isOwn={isOwn}
                        showAvatar={showAvatar}
                        isGroupMessage={isGroup}
                        currentUserId={currentUserId}
                        onReply={handleReply}
                        onEdit={isOwn ? handleEditClick : undefined}
                        onDelete={isOwn ? onDeleteMessage : undefined}
                      />
                    );
                  })}
                </div>

                {/* Date header */}
                <div className="flex items-center justify-center my-4">
                  <div className="px-3 py-1 bg-muted rounded-full">
                    <span className="text-xs text-muted-foreground">
                      {formatDateHeader(group.date)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollToBottom && (
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-24 right-8 rounded-full shadow-lg"
          aria-label="Scroll to latest message"
          onClick={() => scrollToBottom()}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      )}

      {/* Message input */}
      <MessageInput
        onSend={handleSend}
        onTypingStart={onTypingStart}
        onTypingStop={onTypingStop}
        replyTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        isSending={isSending}
        disabled={isArchived}
      />

      {/* Edit Message Modal */}
      <EditMessageModal
        open={!!editingMessage}
        onOpenChange={(open) => !open && setEditingMessage(null)}
        message={editingMessage}
        onSave={handleSaveEdit}
      />

      {/* Export Conversation Modal */}
      <ConversationExport
        open={showExportModal}
        onOpenChange={setShowExportModal}
        conversation={conversation}
        messages={messages}
      />

      {/* Group Info Modal */}
      {isGroup && (
        <GroupInfoModal
          open={showGroupInfo}
          onOpenChange={setShowGroupInfo}
          conversation={conversation}
        />
      )}

      {/* Add Members Modal (quick add for groups) */}
      {conversation && conversation.isGroup && (
        <AddMembersModal
          open={showAddMembersModal}
          onOpenChange={setShowAddMembersModal}
          conversation={conversation}
        />
      )}
    </div>
  );
};

export default MessageThread;
